import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { deleteMailMessage, saveMailDraft } from "@/lib/api/mailActions";
import { scheduleMailSend, type MailScheduledSendEntry } from "@/lib/api/mailScheduledSend";
import {
  mailAttachmentsToComposeAttachments,
  mailAttachmentsToComposeStubs,
  attachmentsForApi,
  payloadAttachmentsToCompose,
} from "@/lib/compose/composeAttachments";
import {
  buildComposeDraft,
  buildComposeDraftFromImapDraft,
  buildComposeDraftFromSummary,
  buildForwardComposeLoadingDraft,
  buildInlineReplyComposeDraft,
} from "@/lib/compose/composeDraft";
import { isInlineReplyMode } from "@/lib/compose/composePlacement";
import { normalizeComposeOutboundBody, stripMailDisplayDocumentWrapper } from "@/lib/compose/composeHtml";
import {
  newComposeSessionId,
  writeComposeSessions,
  type StoredComposeSession,
} from "@/lib/compose/composeSessionsStore";
import { type PendingDraftSave } from "@/lib/compose/draftSavePending";
import { setDraftSaveInFlight } from "@/lib/compose/draftSaveLock";
import { mergeAttachmentFlagsCache } from "@/lib/cache/attachmentFlagsCache";
import { readMessageBodyCache } from "@/lib/cache/messageBodyCache";
import { beginImapExclusiveOp, releaseImapExclusiveOp } from "@/lib/mail/imapClientLock";
import { removeScheduledSendPlaceholders } from "@/lib/outbox/scheduledSendsSessionCache";
import {
  completeMailActivity,
  failMailActivity,
  mailActivitySendLabel,
  mailActivitySubjectLabel,
  startMailActivity,
} from "@/lib/mail/mailActivityJournalNote";
import { resolveDraftsImapFolder, isDraftsNavFolder } from "@/lib/mail/folderNavSections";
import { isMobileMailViewport } from "@/lib/device/isMobileMail";
import { setMailSendBusy } from "@/lib/outbox/mailSendLeave";
import { showMailToast } from "@/lib/ui/mailToast";
import { isUnifiedInboxNavFolder } from "@/lib/unified/constants";
import { isOutboxNavFolder } from "@/lib/outbox/constants";
import { isSnoozedNavFolder } from "@/lib/snooze/constants";
import { flattenFoldersForPicker } from "@/lib/folders/folderTree";
import { isFetchedBodyComplete } from "@/lib/mail/messageBodyCacheStatus";
import {
  canLoadComposeDraftMessage,
  fetchMessageForCompose,
  fetchMessageForComposeDraft,
} from "@/lib/mail/fetchMessageBodyWithCache";
import { readAccountSettings } from "@/lib/mail/accountSettingsPrefs";
import type { OutboxEnqueuePayload, OutboxEnqueueResult, OutboxQueueItem } from "@/lib/outbox/outboxQueue";
import type { ComposeDraft, ComposeMode } from "@/lib/types/compose";
import type { MailAccountSummary, MailFolder, MailMessageDetail, MailMessageSummary } from "@/lib/types/mail";

export type DraftFolderRefreshSpec = {
  accountId: number;
  draftFolder: string;
  folderAtMutation: string;
  /** true = sync IMAP headers + reload forcé (save, delete brouillon). */
  afterSave?: boolean;
};

type Options = {
  accounts: MailAccountSummary[];
  account: MailAccountSummary | undefined;
  selectedFolder: string;
  folders?: MailFolder[];
  getAccountFolders?: (accountId: number) => MailFolder[];
  isOutboxView: boolean;
  displayMessages: MailMessageSummary[];
  readerMessage: MailMessageDetail | null;
  refreshDraftFolder: (spec: DraftFolderRefreshSpec) => Promise<void>;
  removeInboxMessage: (uid: number) => void;
  resolveMessageFolder?: (uid: number, accountId?: number) => string;
  onEnqueue: (payload: OutboxEnqueuePayload) => OutboxEnqueueResult | void;
  onScheduled?: (entry: MailScheduledSendEntry) => void;
  /** Dès la confirmation : fermer la rédaction et afficher la file (spinner). */
  onScheduleBegin?: (info: {
    accountId: number;
    sendAt: string;
    subject: string;
  }) => void;
  /** Fin programmation (OK ou erreur) — coupe le spinner nav. */
  onScheduleEnd?: () => void;
  onSessionChange?: (open: boolean) => void;
  onSavingDraftsChange?: (pending: boolean, accountId?: number) => void;
  onPendingDraftSavesChange?: (pending: PendingDraftSave[]) => void;
  resolveDraftsFolder?: (accountId: number) => string | null;
  /** Desktop : réponse / répondre à tous inline dans le lecteur. */
  inlineReplyEnabled?: boolean;
  /** Compte du message affiché dans le lecteur (réception unifiée). */
  readerAccountId?: number;
  selectedReaderAccountId?: number | null;
  isUnifiedInboxView?: boolean;
  /** UID affiché dans la liste (peut différer du corps IMAP). */
  selectedListUid?: number | null;
};

type ComposeMessageTarget = {
  accountId: number;
  folder: string;
  listUid: number;
  bodyUid: number;
  summary: MailMessageSummary | null;
};

function findComposeSummary(
  displayMessages: MailMessageSummary[],
  listUid: number,
  accountId?: number | null
): MailMessageSummary | null {
  if (accountId != null) {
    return displayMessages.find((m) => m.uid === listUid && m.accountId === accountId) ?? null;
  }
  return displayMessages.find((m) => m.uid === listUid) ?? null;
}

function readerSeedForTarget(
  readerMessage: MailMessageDetail | null,
  readerAccountId: number | undefined,
  target: ComposeMessageTarget,
  selectedListUid: number | null
): MailMessageDetail | null {
  if (!readerMessage || !isFetchedBodyComplete(readerMessage)) return null;
  if (readerAccountId != null && readerAccountId !== target.accountId) return null;

  if (selectedListUid != null && selectedListUid === target.listUid) {
    return readerMessage;
  }

  if (
    readerMessage.uid === target.bodyUid ||
    readerMessage.uid === target.listUid
  ) {
    return readerMessage;
  }

  return null;
}

function readComposeMessageLocal(target: ComposeMessageTarget): MailMessageDetail | null {
  const { accountId, folder, listUid, bodyUid, summary } = target;
  const folders = new Set<string>([folder]);
  const altFolder = summary?.imapFolder?.trim();
  if (altFolder) folders.add(altFolder);

  for (const fold of folders) {
    for (const uid of [bodyUid, listUid]) {
      const hit = readMessageBodyCache(accountId, fold, uid);
      if (hit && isFetchedBodyComplete(hit)) return hit;
    }
  }

  return null;
}

function applyForwardAttachments(
  draft: ComposeDraft,
  message: MailMessageDetail
): ComposeDraft {
  if (!message.hasAttachments || (message.attachments?.length ?? 0) === 0) {
    return draft;
  }
  return {
    ...draft,
    attachments: mailAttachmentsToComposeStubs(message.attachments ?? []),
  };
}

function isVirtualImapFolder(name: string): boolean {
  return (
    isUnifiedInboxNavFolder(name) ||
    isOutboxNavFolder(name) ||
    isSnoozedNavFolder(name)
  );
}

function resolveComposeDraftFolder(
  draft: ComposeDraft,
  sessionDraft: ComposeDraft,
  accountId: number,
  selectedFolder: string,
  getAccountFolders: ((accountId: number) => MailFolder[]) | undefined,
  fallbackFolders: MailFolder[],
  resolveDraftsFolder?: (accountId: number) => string | null
): string | null {
  const explicit = draft.draftFolder?.trim() || sessionDraft.draftFolder?.trim() || "";
  if (explicit && !isVirtualImapFolder(explicit)) {
    return explicit;
  }

  const accountFolders = getAccountFolders?.(accountId) ?? fallbackFolders;

  // Priorité : dossier Brouillons actuellement affiché (évite save ailleurs → liste vide).
  if (!isVirtualImapFolder(selectedFolder)) {
    const folderMeta = flattenFoldersForPicker(accountFolders).find(
      (f) => f.name === selectedFolder
    );
    if (folderMeta && isDraftsNavFolder(folderMeta.name, folderMeta.label)) {
      return selectedFolder;
    }
  }

  const draftsImap =
    resolveDraftsFolder?.(accountId) ?? resolveDraftsImapFolder(accountFolders);
  if (draftsImap) return draftsImap;

  return null;
}

export function useComposeActions({
  accounts,
  account,
  selectedFolder,
  folders = [],
  getAccountFolders,
  isOutboxView,
  displayMessages,
  readerMessage,
  refreshDraftFolder,
  removeInboxMessage,
  resolveMessageFolder,
  onEnqueue,
  onScheduled,
  onScheduleBegin,
  onScheduleEnd,
  onSessionChange,
  onSavingDraftsChange,
  onPendingDraftSavesChange,
  resolveDraftsFolder,
  inlineReplyEnabled = false,
  readerAccountId,
  selectedReaderAccountId = null,
  isUnifiedInboxView = false,
  selectedListUid = null,
}: Options) {
  const [composeSession, setComposeSession] = useState<StoredComposeSession | null>(null);
  const [sendHandoffSessionId, setSendHandoffSessionId] = useState<string | null>(null);
  const [sendLeaveGuard, setSendLeaveGuard] = useState(false);
  const pendingServerAcksRef = useRef(0);
  const [savingDrafts, setSavingDrafts] = useState<PendingDraftSave[]>([]);
  const composeSessionRef = useRef<StoredComposeSession | null>(null);
  composeSessionRef.current = composeSession;

  const resolveComposeAccount = useCallback(
    (accountId?: number) => {
      if (accountId != null) {
        return accounts.find((entry) => entry.id === accountId) ?? account;
      }
      return account;
    },
    [account, accounts]
  );

  const composeAccount = useMemo(
    () => resolveComposeAccount(composeSession?.accountId),
    [composeSession?.accountId, resolveComposeAccount]
  );

  useEffect(() => {
    onPendingDraftSavesChange?.(savingDrafts);
  }, [onPendingDraftSavesChange, savingDrafts]);

  useEffect(() => {
    if (account?.id == null) {
      setComposeSession(null);
      return;
    }
    writeComposeSessions(account.id, []);
  }, [account?.id]);

  useEffect(() => {
    const open = composeSession != null && composeSession.placement !== "inline";
    onSessionChange?.(open);
  }, [composeSession, onSessionChange]);

  useEffect(() => {
    setMailSendBusy(sendLeaveGuard);
  }, [sendLeaveGuard]);

  useEffect(() => () => setMailSendBusy(false), []);

  const clearComposeSession = useCallback(() => {
    setComposeSession(null);
  }, []);

  const openComposeWithMessage = useCallback(
    (mode: ComposeMode, message: MailMessageDetail | null, senderAccount?: MailAccountSummary) => {
      // Nouveau : compte ouvert dans la nav ; défaut seulement si pas de contexte.
      const fromAccount =
        senderAccount ??
        (mode === "new" ? account ?? accounts.find((entry) => entry.isDefault) : account);
      if (!fromAccount) return;

      const settings = readAccountSettings(fromAccount.id);
      const now = Date.now();
      const useInline =
        inlineReplyEnabled && message != null && isInlineReplyMode(mode);
      const draft =
        useInline && (mode === "reply" || mode === "replyAll")
          ? buildInlineReplyComposeDraft(mode, message, {
              accountEmail: fromAccount.email,
              signature: settings.signature,
            })
          : buildComposeDraft(mode, message, {
              accountEmail: fromAccount.email,
              signature: settings.signature,
            });
      setComposeSession({
        id: newComposeSessionId(),
        accountId: fromAccount.id,
        draft,
        createdAt: now,
        updatedAt: now,
        placement: useInline && (mode === "reply" || mode === "replyAll") ? "inline" : "modal",
        inlineReplySourceUid:
          useInline && (mode === "reply" || mode === "replyAll") ? message.uid : undefined,
      });
    },
    [account, accounts, inlineReplyEnabled]
  );

  const resolveComposeTarget = useCallback(
    (listUid: number): ComposeMessageTarget | null => {
      if (!account || listUid < 1) return null;

      const accountHint = isUnifiedInboxView
        ? selectedReaderAccountId ?? readerAccountId ?? null
        : account.id;
      const summary = findComposeSummary(displayMessages, listUid, accountHint);
      const accountId = summary?.accountId ?? accountHint ?? account.id;
      const folder = resolveMessageFolder?.(listUid, accountId) ?? selectedFolder;
      const bodyUid = summary?.imapBodyUid ?? listUid;

      return {
        accountId,
        folder,
        listUid,
        bodyUid,
        summary,
      };
    },
    [
      account,
      displayMessages,
      isUnifiedInboxView,
      readerAccountId,
      resolveMessageFolder,
      selectedFolder,
      selectedReaderAccountId,
    ]
  );

  const openComposeForUid = useCallback(
    async (listUid: number, mode: ComposeMode) => {
      if (!account || isOutboxView) return;

      const target = resolveComposeTarget(listUid);
      if (!target) return;

      const composeAccount = resolveComposeAccount(target.accountId);
      if (!composeAccount) return;

      const seed =
        readerSeedForTarget(readerMessage, readerAccountId, target, selectedListUid) ??
        readComposeMessageLocal(target);

      if (seed && isFetchedBodyComplete(seed)) {
        if (mode !== "forward") {
          openComposeWithMessage(mode, seed, composeAccount);
          return;
        }

        const settings = readAccountSettings(composeAccount.id);
        const draft = applyForwardAttachments(
          buildComposeDraft("forward", seed, {
            accountEmail: composeAccount.email,
            signature: settings.signature,
          }),
          seed
        );
        const now = Date.now();
        const sessionId = newComposeSessionId();
        setComposeSession({
          id: sessionId,
          accountId: composeAccount.id,
          draft,
          createdAt: now,
          updatedAt: now,
          placement: "modal",
        });

        if (
          seed.hasAttachments &&
          (seed.attachments?.length ?? 0) > 0
        ) {
          void mailAttachmentsToComposeAttachments(
            composeAccount.id,
            target.folder,
            target.bodyUid,
            seed.attachments ?? []
          ).then((attachments) => {
            if (attachments.length === 0) return;
            setComposeSession((current) =>
              current?.id === sessionId
                ? {
                    ...current,
                    draft: { ...current.draft, attachments },
                    updatedAt: Date.now(),
                  }
                : current
            );
          });
        }
        return;
      }

      if (mode === "forward") {
        const settings = readAccountSettings(composeAccount.id);
        const now = Date.now();
        const sessionId = newComposeSessionId();
        const subjectSeed = target.summary?.subject ?? seed?.subject ?? "";
        const loadingDraft = buildForwardComposeLoadingDraft(subjectSeed, {
          accountEmail: composeAccount.email,
          signature: settings.signature,
        });
        if (target.summary?.hasAttachments) {
          loadingDraft.attachments = [
            {
              id: `forward-pj-pending-${listUid}`,
              filename: "Pièce jointe…",
              mimeType: "application/octet-stream",
              size: 0,
              contentBase64: "",
              loading: true,
            },
          ];
        }

        setComposeSession({
          id: sessionId,
          accountId: composeAccount.id,
          draft: loadingDraft,
          createdAt: now,
          updatedAt: now,
          placement: "modal",
        });

        const message = await fetchMessageForCompose(
          composeAccount.id,
          target.folder,
          target.bodyUid,
          seed
        );
        if (!message || !isFetchedBodyComplete(message)) {
          window.alert("Impossible de charger le corps du message.");
          setComposeSession((current) => (current?.id === sessionId ? null : current));
          return;
        }

        const draft = applyForwardAttachments(
          buildComposeDraft("forward", message, {
            accountEmail: composeAccount.email,
            signature: settings.signature,
          }),
          message
        );

        setComposeSession((current) =>
          current?.id === sessionId
            ? {
                ...current,
                draft: { ...draft, bodyLoading: false },
                updatedAt: Date.now(),
              }
            : current
        );

        if (message.hasAttachments && (message.attachments?.length ?? 0) > 0) {
          void mailAttachmentsToComposeAttachments(
            composeAccount.id,
            target.folder,
            target.bodyUid,
            message.attachments ?? []
          ).then((attachments) => {
            if (attachments.length === 0) return;
            setComposeSession((current) =>
              current?.id === sessionId
                ? {
                    ...current,
                    draft: { ...current.draft, attachments },
                    updatedAt: Date.now(),
                  }
                : current
            );
          });
        }
        return;
      }

      const message = await fetchMessageForCompose(
        composeAccount.id,
        target.folder,
        target.bodyUid,
        seed
      );
      if (!message || !isFetchedBodyComplete(message)) {
        window.alert("Impossible de charger le corps du message.");
        return;
      }
      openComposeWithMessage(mode, message, composeAccount);
    },
    [
      account,
      isOutboxView,
      openComposeWithMessage,
      readerAccountId,
      readerMessage,
      resolveComposeAccount,
      resolveComposeTarget,
      selectedListUid,
    ]
  );

  const openCompose = useCallback(
    (mode: ComposeMode) => {
      if (!account) return;
      if (mode === "new") {
        openComposeWithMessage("new", null);
        return;
      }
      const listUid = selectedListUid ?? readerMessage?.uid;
      if (listUid == null || listUid < 1) return;
      void openComposeForUid(listUid, mode);
    },
    [account, openComposeForUid, openComposeWithMessage, readerMessage?.uid, selectedListUid]
  );

  const openDraftForEdit = useCallback(
    (uid: number) => {
      if (!account || isOutboxView) return;

      const summary = displayMessages.find((m) => m.uid === uid);
      const folderAtOpen = summary?.imapFolder ?? selectedFolder;
      const now = Date.now();
      const sessionId = newComposeSessionId();

      setComposeSession({
        id: sessionId,
        accountId: account.id,
        draft: {
          ...buildComposeDraftFromSummary(
            { subject: summary?.subject ?? "" },
            folderAtOpen,
            uid
          ),
          attachments: summary?.hasAttachments
            ? [
                {
                  id: `draft-pj-pending-${uid}`,
                  filename: "Pièce jointe…",
                  mimeType: "application/octet-stream",
                  size: 0,
                  contentBase64: "",
                  loading: true,
                },
              ]
            : undefined,
        },
        createdAt: now,
        updatedAt: now,
      });

      void (async () => {
        let message =
          readerMessage?.uid === uid &&
          readerMessage &&
          canLoadComposeDraftMessage(readerMessage)
            ? readerMessage
            : await fetchMessageForComposeDraft(account.id, folderAtOpen, uid);

        if (!message) {
          window.alert("Impossible de charger le brouillon.");
          setComposeSession((current) => (current?.id === sessionId ? null : current));
          return;
        }

        const attachmentStubs = mailAttachmentsToComposeStubs(message.attachments ?? []);

        setComposeSession((current) =>
          current?.id === sessionId
            ? {
                ...current,
                draft: buildComposeDraftFromImapDraft(
                  message,
                  folderAtOpen,
                  uid,
                  attachmentStubs
                ),
                updatedAt: Date.now(),
              }
            : current
        );

        if (!message.attachments?.length) return;

        const composeAttachments = await mailAttachmentsToComposeAttachments(
          account.id,
          folderAtOpen,
          uid,
          message.attachments
        );

        setComposeSession((current) =>
          current?.id === sessionId
            ? {
                ...current,
                draft: {
                  ...current.draft,
                  attachments: composeAttachments.length > 0 ? composeAttachments : undefined,
                },
                updatedAt: Date.now(),
              }
            : current
        );
      })();
    },
    [account, displayMessages, isOutboxView, readerMessage, selectedFolder]
  );

  const changeComposeAccount = useCallback(
    (sessionId: string, accountId: number) => {
      setComposeSession((current) => {
        if (!current || current.id !== sessionId || current.draft.mode !== "new") {
          return current;
        }
        const nextAccount = accounts.find((entry) => entry.id === accountId);
        if (!nextAccount || nextAccount.id === current.accountId) return current;

        const oldAccountId = current.accountId;
        const draftFolder = current.draft.draftFolder;
        const draftUid = current.draft.draftUid;
        if (draftFolder && draftUid != null && draftUid > 0) {
          void deleteMailMessage({
            accountId: oldAccountId,
            folder: draftFolder,
            uid: draftUid,
          }).then((res) => {
            if (res.ok) {
              void refreshDraftFolder({
                accountId: oldAccountId,
                draftFolder,
                folderAtMutation: selectedFolder,
                afterSave: true,
              });
            }
          });
        }

        return {
          ...current,
          accountId: nextAccount.id,
          draft: {
            ...current.draft,
            draftFolder: undefined,
            draftUid: undefined,
          },
          updatedAt: Date.now(),
        };
      });
    },
    [accounts, refreshDraftFolder, selectedFolder]
  );

  const updateComposeDraft = useCallback((sessionId: string, patch: Partial<ComposeDraft>) => {
    setComposeSession((current) =>
      current && current.id === sessionId
        ? { ...current, draft: { ...current.draft, ...patch }, updatedAt: Date.now() }
        : current
    );
  }, []);

  const closeComposeSession = useCallback((sessionId: string) => {
    setComposeSession((current) => (current?.id === sessionId ? null : current));
  }, []);

  const handleSaveComposeDraft = useCallback(
    (sessionId: string, draft: ComposeDraft) => {
      const sessionAccount = resolveComposeAccount(composeSession?.accountId);
      if (!sessionAccount || !composeSession || composeSession.id !== sessionId) return;

      const accountId = sessionAccount.id;
      const folderAtSave = selectedFolder;
      const pendingId = crypto.randomUUID();
      const composeSnapshot = composeSession;
      const targetDraftFolder =
        resolveComposeDraftFolder(
          draft,
          composeSession.draft,
          accountId,
          selectedFolder,
          getAccountFolders,
          folders,
          resolveDraftsFolder
        ) ??
        resolveDraftsFolder?.(accountId) ??
        draft.draftFolder;

      onSavingDraftsChange?.(true, accountId);
      setDraftSaveInFlight(true);
      beginImapExclusiveOp();
      setSavingDrafts((prev) => [
        ...prev,
        {
          id: pendingId,
          folder: draft.draftFolder ?? selectedFolder,
          uid: draft.draftUid,
          subject: draft.subject,
          to: draft.to,
          hasAttachments:
            draft.attachments?.some((file) => !file.loading || Boolean(file.contentBase64)) ===
            true,
        },
      ]);
      setComposeSession(null);

      const journalId = startMailActivity(
        mailActivitySubjectLabel("Brouillon en cours", draft.subject),
        "other",
        "local"
      );

      void (async () => {
        try {
          const { bodyHtml, bodyText } = normalizeComposeOutboundBody(
            draft.bodyHtml,
            draft.bodyText
          );
          const res = await saveMailDraft({
            accountId,
            draftFolder: targetDraftFolder ?? undefined,
            folder: draft.draftFolder,
            uid: draft.draftUid,
            to: draft.to,
            cc: draft.cc,
            subject: draft.subject,
            bodyText,
            bodyHtml,
            attachments: attachmentsForApi(draft.attachments),
            inReplyTo: draft.inReplyTo,
            references: draft.references,
          });

          if (!res.ok) {
            failMailActivity(journalId, res.message);
            window.alert(res.message);
            setSavingDrafts((prev) => prev.filter((entry) => entry.id !== pendingId));
            setComposeSession(composeSnapshot);
            return;
          }

          const savedFolder = res.data.draftFolder;
          const savedUid = res.data.draftUid;
          const savedHasAttachments =
            draft.attachments?.some((file) => !file.loading || Boolean(file.contentBase64)) ===
            true;
          if (savedUid > 0 && savedHasAttachments) {
            mergeAttachmentFlagsCache(
              accountId,
              savedFolder,
              new Map([[savedUid, true]])
            );
          }
          setSavingDrafts((prev) =>
            prev.map((entry) =>
              entry.id === pendingId
                ? {
                    ...entry,
                    folder: savedFolder,
                    uid: savedUid,
                    hasAttachments: savedHasAttachments,
                  }
                : entry
            )
          );

          await refreshDraftFolder({
            accountId,
            draftFolder: savedFolder,
            folderAtMutation: folderAtSave,
            afterSave: true,
          });
          setSavingDrafts((prev) => prev.filter((entry) => entry.id !== pendingId));
          completeMailActivity(
            journalId,
            mailActivitySubjectLabel("Brouillon enregistré", draft.subject)
          );
        } catch (err) {
          failMailActivity(
            journalId,
            err instanceof Error ? err.message : "Impossible d'enregistrer le brouillon."
          );
          setSavingDrafts((prev) => prev.filter((entry) => entry.id !== pendingId));
        } finally {
          releaseImapExclusiveOp();
          setDraftSaveInFlight(false);
          onSavingDraftsChange?.(false, accountId);
        }
      })();
    },
    [composeSession, folders, getAccountFolders, onSavingDraftsChange, refreshDraftFolder, resolveComposeAccount, resolveDraftsFolder, selectedFolder]
  );

  const handleDiscardComposeDraft = useCallback(
    (sessionId: string, draft: ComposeDraft) => {
      const activeSession = composeSessionRef.current;
      if (!activeSession || activeSession.id !== sessionId) return;

      const sessionAccount = resolveComposeAccount(activeSession.accountId);
      if (!sessionAccount) {
        setComposeSession(null);
        return;
      }

      const accountId = sessionAccount.id;
      const draftUid = draft.draftUid ?? activeSession.draft.draftUid;
      const draftFolder = resolveComposeDraftFolder(
        draft,
        activeSession.draft,
        accountId,
        selectedFolder,
        getAccountFolders,
        folders,
        resolveDraftsFolder
      );

      setComposeSession(null);

      if (draftUid == null || draftUid < 1 || !draftFolder) return;

      const pendingId = crypto.randomUUID();
      setSavingDrafts((prev) => [
        ...prev,
        {
          id: pendingId,
          folder: draftFolder,
          uid: draftUid,
          subject: draft.subject,
          to: draft.to,
          deleting: true,
        },
      ]);

      void deleteMailMessage({ accountId, folder: draftFolder, uid: draftUid })
        .then((res) => {
          if (!res.ok) {
            window.alert(res.message ?? "Impossible de supprimer le brouillon.");
            void refreshDraftFolder({
              accountId,
              draftFolder,
              folderAtMutation: selectedFolder,
              afterSave: true,
            });
            return;
          }
          removeInboxMessage(draftUid);
          // Pas afterSave:true — évite un sync IMAP immédiat qui réinjecte le draft
          // dans DD02 si le delete serveur a été ambigu.
          void refreshDraftFolder({
            accountId,
            draftFolder,
            folderAtMutation: selectedFolder,
            afterSave: false,
          });
        })
        .finally(() => {
          setSavingDrafts((prev) => prev.filter((entry) => entry.id !== pendingId));
        });
    },
    [folders, getAccountFolders, refreshDraftFolder, removeInboxMessage, resolveComposeAccount, resolveDraftsFolder, selectedFolder]
  );

  const openComposeFromOutbox = useCallback(
    (item: OutboxQueueItem) => {
      if (!account) return;

      const now = Date.now();
      setComposeSession({
        id: newComposeSessionId(),
        accountId: account.id,
        draft: {
          mode: "new",
          to: item.to,
          cc: item.cc ?? "",
          bcc: item.bcc ?? "",
          subject: item.subject,
          bodyText: item.bodyText,
          bodyHtml: stripMailDisplayDocumentWrapper(item.bodyHtml ?? "") || item.bodyText,
          attachments: payloadAttachmentsToCompose(item.attachments),
          inReplyTo: item.inReplyTo,
          references: item.references,
        },
        createdAt: now,
        updatedAt: now,
      });
    },
    [account]
  );

  const handleComposeEnqueue = useCallback(
    (sessionId: string, payload: OutboxEnqueuePayload) => {
      const draft = composeSession?.id === sessionId ? composeSession.draft : null;
      const sessionAccount = resolveComposeAccount(composeSession?.accountId ?? payload.accountId);
      const accountId = sessionAccount?.id ?? payload.accountId;
      const draftFolder = draft?.draftFolder;
      const draftUid = draft?.draftUid;
      const queued = onEnqueue({
        ...payload,
        fromEmail: payload.fromEmail ?? sessionAccount?.email,
      });

      if (queued && queued.ok === false) {
        return;
      }
      if (!queued || queued.ok !== true || !queued.accepted) {
        return;
      }

      setSendHandoffSessionId(sessionId);
      pendingServerAcksRef.current += 1;
      setSendLeaveGuard(true);
      if (!isMobileMailViewport()) {
        setComposeSession((current) => (current?.id === sessionId ? null : current));
        showMailToast("Envoi en cours.", "info");
      }
      void queued.accepted.then((ack) => {
        pendingServerAcksRef.current = Math.max(0, pendingServerAcksRef.current - 1);
        if (pendingServerAcksRef.current === 0) setSendLeaveGuard(false);
        setSendHandoffSessionId((current) => (current === sessionId ? null : current));
        if (!ack.ok) {
          showMailToast(ack.error, "error", 9000);
          return;
        }

        setComposeSession((current) => (current?.id === sessionId ? null : current));

        if (accountId != null && draftFolder && draftUid != null && draftUid > 0) {
          void deleteMailMessage({ accountId, folder: draftFolder, uid: draftUid }).then((res) => {
            if (!res.ok) return;
            void refreshDraftFolder({
              accountId,
              draftFolder,
              folderAtMutation: selectedFolder,
              afterSave: true,
            });
          });
        }
      });
    },
    [composeSession, onEnqueue, refreshDraftFolder, resolveComposeAccount, selectedFolder]
  );

  const handleScheduleCompose = useCallback(
    (sessionId: string, payload: OutboxEnqueuePayload, sendAt: string) => {
      const activeSession = composeSession;
      if (!activeSession || activeSession.id !== sessionId) return;

      const sessionAccount = resolveComposeAccount(activeSession.accountId ?? payload.accountId);
      if (!sessionAccount) return;

      const accountId = sessionAccount.id;
      const draft = activeSession.draft;
      const draftFolder = draft.draftFolder;
      const draftUid = draft.draftUid;

      // UX : fermer la rédaction + spinner sur la ligne « Envoi programmé » (comme brouillon).
      setComposeSession((current) => (current?.id === sessionId ? null : current));
      onScheduleBegin?.({
        accountId,
        sendAt,
        subject: payload.subject ?? "",
      });

      const journalId = startMailActivity(
        mailActivitySendLabel({
          prefix: "Programmation",
          subject: payload.subject,
          from: sessionAccount.email,
          to: payload.to,
          cc: payload.cc,
        }),
        "send",
        "local"
      );

      void (async () => {
        try {
          const res = await scheduleMailSend({
            accountId,
            sendAt,
            to: payload.to,
            cc: payload.cc,
            bcc: payload.bcc,
            subject: payload.subject,
            bodyText: payload.bodyText,
            bodyHtml: payload.bodyHtml,
            attachments: payload.attachments,
            inReplyTo: payload.inReplyTo,
            references: payload.references,
          });

          if (!res.ok) {
            failMailActivity(journalId, res.message);
            removeScheduledSendPlaceholders(accountId);
            onScheduleEnd?.();
            window.alert(res.message);
            return;
          }

          const scheduled = res.data.scheduled;
          if (scheduled?.id) {
            onScheduled?.(scheduled);
          }
          onScheduleEnd?.();
          completeMailActivity(
            journalId,
            mailActivitySendLabel({
              prefix: "Programmé",
              subject: payload.subject,
              from: sessionAccount.email,
              to: payload.to,
              cc: payload.cc,
            })
          );

          if (draftFolder && draftUid != null && draftUid > 0) {
            void deleteMailMessage({ accountId, folder: draftFolder, uid: draftUid }).then((del) => {
              if (!del.ok) return;
              void refreshDraftFolder({
                accountId,
                draftFolder,
                folderAtMutation: selectedFolder,
                afterSave: true,
              });
            });
          }
        } catch (err) {
          failMailActivity(
            journalId,
            err instanceof Error ? err.message : "Impossible de programmer l'envoi."
          );
          removeScheduledSendPlaceholders(accountId);
          onScheduleEnd?.();
          window.alert(
            err instanceof Error ? err.message : "Impossible de programmer l'envoi."
          );
        }
      })();
    },
    [
      composeSession,
      onScheduleBegin,
      onScheduleEnd,
      onScheduled,
      refreshDraftFolder,
      resolveComposeAccount,
      selectedFolder,
    ]
  );

  return {
    composeSession,
    sendHandoffSessionId,
    composeAccount,
    savingDrafts,
    clearComposeSession,
    openCompose,
    openComposeFromOutbox,
    openDraftForEdit,
    openComposeForUid,
    changeComposeAccount,
    updateComposeDraft,
    closeComposeSession,
    handleSaveComposeDraft,
    handleDiscardComposeDraft,
    handleComposeEnqueue,
    handleScheduleCompose,
  };
}
